/**
 * EAA-1822: shared shape for the edit (frame 19) and onboarding (frame 20)
 * forms — both collect a subset of the same fields.
 *
 * IMPORTANT — there is currently NO portal-api endpoint that reads or writes
 * any of these fields. Checked `services/portal-api/portal_api/directory/
 * models.py` (every model in the app: Application, ExternalSystem,
 * Organization, OrganizationAdmin, AccountAudit, AdminAudit,
 * OrganizationAppScope, AppRole, Level, RoleMap, Assignment, RuntimeConfig(+
 * Change), ErasureRequest, Notification) — none of them is a per-user profile
 * row, and D10 (`emg_shared` kernel purity doc) is explicit that the identity
 * kernel carries only sub/email/name/sid, never more. So `ho`/`ten` are
 * seeded from the OIDC `name` claim (best-effort split, see `nameSplit.ts`)
 * and everything else starts blank — never a fake seeded value.
 */
export interface ProfileFormValues {
    ho: string;
    ten: string;
    phone: string;
    cccd: string;
    /** No province/ward source exists anywhere in portal-api — always ''. */
    tinh: string;
    phuong: string;
    diaChiCuThe: string;
    ngaySinh: string;
    /**
     * '' | 'nu' | 'nam' | 'khac'. No backend enum exists to source these
     * values from (no field, no catalog) — this is a UI-only assumption,
     * flagged in the EAA-1822 report rather than presented as a confirmed
     * contract.
     */
    gioiTinh: '' | 'nu' | 'nam' | 'khac';
}

export const EMPTY_PROFILE_FORM: ProfileFormValues = {
    ho: '',
    ten: '',
    phone: '',
    cccd: '',
    tinh: '',
    phuong: '',
    diaChiCuThe: '',
    ngaySinh: '',
    gioiTinh: '',
};

export const GENDER_OPTIONS: Array<{ value: ProfileFormValues['gioiTinh']; label: string }> = [
    { value: 'nu', label: 'Nữ' },
    { value: 'nam', label: 'Nam' },
    { value: 'khac', label: 'Khác' },
];

/** Họ, Tên, Email, Giới tính — per the ticket's required-field list (frame 19/20 both mark these `*`). */
export function countMissingRequired(values: ProfileFormValues, hasEmail: boolean): number {
    let missing = 0;
    if (!values.ho.trim()) missing += 1;
    if (!values.ten.trim()) missing += 1;
    if (!values.gioiTinh) missing += 1;
    if (!hasEmail) missing += 1;
    return missing;
}

/**
 * EAA-1822: "Masked to the last 4 digits" (UX-SPEC.md §/profile — CMND/CCCD
 * handling) — used to mask the CMND/CCCD control whenever it is not focused,
 * in both the edit view and the onboarding step-2 field. This is a DISPLAY
 * mask only: there is no portal-api endpoint that persists this value at all
 * (see this file's module doc comment), so the spec's "full value visible
 * only to the profile owner and school admins, every reveal audited" access
 * rule has nothing server-side to enforce yet — that part of the spec cannot
 * be implemented honestly until a real field + endpoint exist.
 */
export function maskCccdDisplay(raw: string): string {
    const digits = raw.trim();
    if (digits.length <= 4) return digits;
    return '•'.repeat(digits.length - 4) + digits.slice(-4);
}
